/**
 * On-canvas editing gizmos for parametric primitives.
 *
 * Ovals   – arc sweep/start and inner-ratio (donut) handles
 * Polygons – side count and corner curvature handles
 * Stars   – point count and inner-radius ratio handles
 */

import type { OvalPrimitive, PolygonPrimitive, StarPrimitive } from "@/document/model/primitives";
import type { PrimitiveNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";

// ─── Handle identity types ───────────────────────────────────────────────────

export type PrimitiveGizmoHandle =
  | { type: "oval:arcEnd" }      // outer ellipse at arcStart + arcSweep → controls sweep
  | { type: "oval:arcStart" }    // outer ellipse at arcStart → controls start angle
  | { type: "oval:ratio" }       // inner ellipse at midpoint angle → controls ratio
  | { type: "polygon:count" }    // top outer vertex → controls sides
  | { type: "polygon:curvature" }// corner-arc handle near first vertex → controls curvature
  | { type: "star:count" }       // first outer vertex → controls points
  | { type: "star:ratio" };      // first inner vertex → controls innerRadiusRatio

export interface PrimitiveGizmoPoint {
  handle: PrimitiveGizmoHandle;
  x: number;        // world / document space
  y: number;
}

// ─── Geometry ────────────────────────────────────────────────────────────────

/** Inset a gizmo point toward the shape center by `insetDoc` document units. */
function insetTowardCenter(point: PrimitiveGizmoPoint, cx: number, cy: number, insetDoc: number): PrimitiveGizmoPoint {
  const dx = point.x - cx;
  const dy = point.y - cy;
  const dist = Math.hypot(dx, dy);
  if (dist <= 0.001) return point;
  const inset = Math.min(insetDoc, dist * 0.5);
  return { ...point, x: point.x - (dx / dist) * inset, y: point.y - (dy / dist) * inset };
}

/**
 * Returns gizmo handle positions in document/world space.
 *
 * Pass `zoom` so handles are inset slightly inward (by `GIZMO_INSET_PX / zoom`
 * in document units) to avoid overlapping the resize handles on the bounding box.
 * Omit `zoom` (or pass 0) to get the raw boundary-snapped positions used internally
 * for drag transform computations.
 */
export function primitiveGizmoPoints(node: PrimitiveNode, zoom?: number): PrimitiveGizmoPoint[] {
  const { bounds, primitive } = node;
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;

  let points: PrimitiveGizmoPoint[];
  switch (primitive.kind) {
    case "oval":
      points = ovalGizmoPoints(bounds, primitive);
      break;
    case "polygon":
      points = polygonGizmoPoints(bounds, primitive);
      break;
    case "star":
      points = starGizmoPoints(bounds, primitive);
      break;
    default:
      return [];
  }

  if (zoom && zoom > 0) {
    const insetDoc = GIZMO_INSET_PX / zoom;
    return points.map(p => insetTowardCenter(p, cx, cy, insetDoc));
  }
  return points;
}

function ovalGizmoPoints(bounds: Rect, prim: OvalPrimitive): PrimitiveGizmoPoint[] {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = Math.abs(bounds.width) / 2;
  const ry = Math.abs(bounds.height) / 2;

  const arcStart = prim.arcStart ?? 0;
  const arcSweep = prim.arcSweep ?? 360;
  const ratio     = prim.ratio ?? 0;

  const endAngleRad = (arcStart + arcSweep - 90) * (Math.PI / 180);
  const midAngleRad = (arcStart + arcSweep / 2 - 90) * (Math.PI / 180);

  const points: PrimitiveGizmoPoint[] = [];

  // Arc-end handle (always shown)
  points.push({
    handle: { type: "oval:arcEnd" },
    x: cx + Math.cos(endAngleRad) * rx,
    y: cy + Math.sin(endAngleRad) * ry,
  });

  // Arc-start handle (only when arc is active)
  if (arcSweep < 360) {
    const startAngleRad = (arcStart - 90) * (Math.PI / 180);
    points.push({
      handle: { type: "oval:arcStart" },
      x: cx + Math.cos(startAngleRad) * rx,
      y: cy + Math.sin(startAngleRad) * ry,
    });
  }

  // Ratio handle (always shown – at midpoint of arc on the inner ellipse boundary)
  points.push({
    handle: { type: "oval:ratio" },
    x: cx + Math.cos(midAngleRad) * rx * Math.max(0, ratio),
    y: cy + Math.sin(midAngleRad) * ry * Math.max(0, ratio),
  });

  return points;
}

function polygonGizmoPoints(bounds: Rect, prim: PolygonPrimitive): PrimitiveGizmoPoint[] {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const radius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  const safeSides = Math.max(3, Math.round(prim.sides));
  const curvature = prim.curvature ?? 0;

  // Top vertex (12 o'clock)
  const topAngle = -Math.PI / 2;
  const topX = cx + Math.cos(topAngle) * radius;
  const topY = cy + Math.sin(topAngle) * radius;

  const points: PrimitiveGizmoPoint[] = [
    { handle: { type: "polygon:count" }, x: topX, y: topY },
  ];

  // Curvature handle: inset from top vertex along the edge to the next vertex
  const nextAngle = topAngle + (Math.PI * 2) / safeSides;
  const nextX = cx + Math.cos(nextAngle) * radius;
  const nextY = cy + Math.sin(nextAngle) * radius;
  const edgeLen = Math.hypot(nextX - topX, nextY - topY);
  const maxOffset = edgeLen / 2;
  const offset = maxOffset * Math.max(0, Math.min(1, curvature));
  const dx = (nextX - topX) / Math.max(0.001, edgeLen);
  const dy = (nextY - topY) / Math.max(0.001, edgeLen);

  points.push({
    handle: { type: "polygon:curvature" },
    x: topX + dx * offset,
    y: topY + dy * offset,
  });

  return points;
}

function starGizmoPoints(bounds: Rect, prim: StarPrimitive): PrimitiveGizmoPoint[] {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const outerRadius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  const safePoints = Math.max(3, Math.round(prim.points));
  const safeRatio  = Math.max(0.05, Math.min(0.95, prim.innerRadiusRatio));
  const innerRadius = outerRadius * safeRatio;

  // First outer vertex at top (-90°)
  const outerAngle = -Math.PI / 2;
  // First inner vertex at (outerAngle + half step)
  const innerAngle = outerAngle + Math.PI / safePoints;

  return [
    {
      handle: { type: "star:count" },
      x: cx + Math.cos(outerAngle) * outerRadius,
      y: cy + Math.sin(outerAngle) * outerRadius,
    },
    {
      handle: { type: "star:ratio" },
      x: cx + Math.cos(innerAngle) * innerRadius,
      y: cy + Math.sin(innerAngle) * innerRadius,
    },
  ];
}

// ─── Hit testing ─────────────────────────────────────────────────────────────

const GIZMO_HIT_RADIUS_PX = 8;
/** How far (in screen px) gizmo handles are pushed inward from the shape boundary to avoid overlapping resize handles. */
const GIZMO_INSET_PX = 14;

export function hitTestPrimitiveGizmo(
  node: PrimitiveNode,
  point: { x: number; y: number },
  zoom: number,
): PrimitiveGizmoHandle | undefined {
  const hitRadius = GIZMO_HIT_RADIUS_PX / zoom;
  const gizmos = primitiveGizmoPoints(node, zoom);

  for (const g of gizmos) {
    if (Math.hypot(point.x - g.x, point.y - g.y) <= hitRadius) {
      return g.handle;
    }
  }

  return undefined;
}

// ─── Primitive transforms ────────────────────────────────────────────────────

/** Drag the oval arc-end handle to a new world point. */
export function ovalWithArcEndDrag(prim: OvalPrimitive, bounds: Rect, point: { x: number; y: number }): OvalPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const arcStart = prim.arcStart ?? 0;

  const angle = Math.atan2(point.y - cy, point.x - cx) * (180 / Math.PI) + 90;
  const normalizedAngle = ((angle % 360) + 360) % 360;

  // Sweep = normalizedAngle measured clockwise from arcStart
  let sweep = ((normalizedAngle - ((arcStart % 360 + 360) % 360)) + 360) % 360;
  if (sweep < 1) sweep = 360; // snap to full circle when near 0

  return { ...prim, arcSweep: Math.round(sweep * 10) / 10 };
}

/** Drag the oval arc-start handle to a new world point. */
export function ovalWithArcStartDrag(prim: OvalPrimitive, bounds: Rect, point: { x: number; y: number }): OvalPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const arcSweep = prim.arcSweep ?? 360;

  const angle = Math.atan2(point.y - cy, point.x - cx) * (180 / Math.PI) + 90;
  const newStart = ((angle % 360) + 360) % 360;

  return { ...prim, arcStart: Math.round(newStart * 10) / 10, arcSweep };
}

/** Drag the oval ratio handle to a new world point. */
export function ovalWithRatioDrag(prim: OvalPrimitive, bounds: Rect, point: { x: number; y: number }): OvalPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const rx = Math.abs(bounds.width) / 2;
  const ry = Math.abs(bounds.height) / 2;

  if (rx < 0.001 || ry < 0.001) return prim;

  const dx = (point.x - cx) / rx;
  const dy = (point.y - cy) / ry;
  // Use geometric mean of normalised x/y to compute ratio
  const dist = Math.hypot(dx, dy);
  const ratio = Math.max(0, Math.min(0.99, Math.round(dist * 1000) / 1000));

  return { ...prim, ratio };
}

/** Drag the polygon count handle up/down relative to start to change side count. */
export function polygonWithCountDrag(
  prim: PolygonPrimitive,
  bounds: Rect,
  point: { x: number; y: number },
  startPoint: { x: number; y: number },
  startSides: number,
): PolygonPrimitive {
  const radius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  if (radius < 1) return prim;

  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;

  // Angle of start point and current point around center
  const startAngle = Math.atan2(startPoint.y - cy, startPoint.x - cx);
  const currAngle  = Math.atan2(point.y - cy, point.x - cx);
  let delta = currAngle - startAngle;
  // Normalise to [-pi, pi]
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;

  // Map ~10° of rotation to one side
  const sidesDelta = Math.round(delta / (Math.PI / 18));
  const newSides = Math.max(3, Math.min(64, startSides + sidesDelta));

  return { ...prim, sides: newSides };
}

/** Drag the polygon curvature handle along the edge to set curvature. */
export function polygonWithCurvatureDrag(
  prim: PolygonPrimitive,
  bounds: Rect,
  point: { x: number; y: number },
): PolygonPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const radius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  const safeSides = Math.max(3, Math.round(prim.sides));

  const topAngle  = -Math.PI / 2;
  const nextAngle = topAngle + (Math.PI * 2) / safeSides;
  const topX = cx + Math.cos(topAngle) * radius;
  const topY = cy + Math.sin(topAngle) * radius;
  const nextX = cx + Math.cos(nextAngle) * radius;
  const nextY = cy + Math.sin(nextAngle) * radius;
  const edgeLen = Math.hypot(nextX - topX, nextY - topY);
  const maxOffset = edgeLen / 2;

  if (maxOffset < 0.001) return prim;

  // Project point onto the top→next edge
  const dx = (nextX - topX) / edgeLen;
  const dy = (nextY - topY) / edgeLen;
  const proj = (point.x - topX) * dx + (point.y - topY) * dy;
  const curvature = Math.max(0, Math.min(1, Math.round(proj / maxOffset * 1000) / 1000));

  return { ...prim, curvature };
}

/** Drag the star count handle (angle around center → new point count). */
export function starWithCountDrag(
  prim: StarPrimitive,
  bounds: Rect,
  point: { x: number; y: number },
  startPoint: { x: number; y: number },
  startPoints: number,
): StarPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;

  const startAngle = Math.atan2(startPoint.y - cy, startPoint.x - cx);
  const currAngle  = Math.atan2(point.y - cy, point.x - cx);
  let delta = currAngle - startAngle;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;

  const sidesDelta = Math.round(delta / (Math.PI / 18));
  const newPoints = Math.max(3, Math.min(64, startPoints + sidesDelta));

  return { ...prim, points: newPoints };
}

/** Drag the star ratio handle inward/outward to change inner radius ratio. */
export function starWithRatioDrag(prim: StarPrimitive, bounds: Rect, point: { x: number; y: number }): StarPrimitive {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const outerRadius = Math.min(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;

  if (outerRadius < 1) return prim;

  const dist = Math.hypot(point.x - cx, point.y - cy);
  const ratio = Math.max(0.05, Math.min(0.95, Math.round(dist / outerRadius * 100) / 100));

  return { ...prim, innerRadiusRatio: ratio };
}

// ─── Tooltip labels ──────────────────────────────────────────────────────────

export function primitiveGizmoLabel(primitive: PrimitiveNode["primitive"], handle: PrimitiveGizmoHandle): string {
  if (handle.type === "oval:arcEnd" || handle.type === "oval:arcStart") {
    if (primitive.kind !== "oval") return "";
    const sweep = primitive.arcSweep ?? 360;
    return `${Math.round(sweep)}°`;
  }

  if (handle.type === "oval:ratio") {
    if (primitive.kind !== "oval") return "";
    const ratio = primitive.ratio ?? 0;
    return `${Math.round(ratio * 100)}%`;
  }

  if (handle.type === "polygon:count") {
    if (primitive.kind !== "polygon") return "";
    return `Count ${Math.round(primitive.sides)}`;
  }

  if (handle.type === "polygon:curvature") {
    if (primitive.kind !== "polygon") return "";
    const c = primitive.curvature ?? 0;
    return `Radius ${Math.round(c * 100) / 100}`;
  }

  if (handle.type === "star:count") {
    if (primitive.kind !== "star") return "";
    return `Count ${Math.round(primitive.points)}`;
  }

  if (handle.type === "star:ratio") {
    if (primitive.kind !== "star") return "";
    const r = primitive.innerRadiusRatio;
    return `Ratio ${Math.round(r * 100)}%`;
  }

  return "";
}
